import { supabase } from "./supabase";
import type { SongRow } from "./analysis";

export interface Setlist {
  id: string; owner: string; name: string; venue: string | null;
  plays_on: string | null; share_token: string | null; updated_at: string;
}

export interface SetlistItem {
  setlist_id: string; position: number; song_id: string;
  transpose: number; capo: number | null; note: string | null;
  songs?: SongRow;                       // joined
}

export async function mySetlists(): Promise<Setlist[]> {
  const { data } = await supabase.from("setlists").select("*")
    .order("updated_at", { ascending: false });
  return (data ?? []) as Setlist[];
}

export async function getSetlist(id: string, token?: string | null) {
  // A shared link authorises by handing the token in, not by filtering on it:
  // a filter in a REST query is something anyone can write, so it can never be
  // a credential. Owners and members go through the table as usual.
  if (token) {
    const [list, items] = await Promise.all([
      supabase.rpc("setlist_by_token", { p_id: id, p_token: token }),
      supabase.rpc("setlist_items_by_token", { p_id: id, p_token: token }),
    ]);
    const rows = (items.data ?? []) as SetlistItem[];
    const songIds = [...new Set(rows.map((row) => row.song_id))];
    const { data: songs } = songIds.length
      ? await supabase.from("songs").select("*").in("id", songIds)
      : { data: [] };
    const bySong = new Map((songs ?? []).map((song: any) => [song.id, song]));
    return {
      list: ((list.data ?? [])[0] ?? null) as Setlist | null,
      items: rows.map((row) => ({ ...row, songs: bySong.get(row.song_id) })) as SetlistItem[],
    };
  }

  const [list, items] = await Promise.all([
    supabase.from("setlists").select("*").eq("id", id).maybeSingle(),
    supabase.from("setlist_items")
      .select("*, songs(*)").eq("setlist_id", id).order("position"),
  ]);
  return {
    list: list.data as Setlist | null,
    items: (items.data ?? []) as SetlistItem[],
  };
}

export async function createSetlist(name: string, venue?: string) {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return null;
  // owner defaults to auth.uid() in the database. Sending it from here meant
  // any drift between the cached session and the request's JWT came back as a
  // bare row-level-security rejection.
  const { data, error } = await supabase.from("setlists")
    .insert({ name, venue: venue ?? null })
    .select().single();
  if (error) throw error;
  return data as Setlist | null;
}

export async function addToSetlist(setlistId: string, songId: string) {
  // Append: ask for the current tail rather than counting client-side, so two
  // people adding at once cannot land on the same position.
  const { data: last } = await supabase.from("setlist_items")
    .select("position").eq("setlist_id", setlistId)
    .order("position", { ascending: false }).limit(1).maybeSingle();
  const position = ((last?.position as number | undefined) ?? 0) + 1;
  await supabase.from("setlist_items")
    .insert({ setlist_id: setlistId, song_id: songId, position });
}

/**
 * Move an item one place. Positions are rewritten for the whole list rather
 * than swapped in place: swapping two rows in a table with a composite primary
 * key needs a temporary value to dodge the uniqueness constraint, and a full
 * rewrite of a dozen rows is simpler to reason about than that dance.
 */
export async function moveItem(items: SetlistItem[], from: number, direction: -1 | 1) {
  const to = from + direction;
  if (to < 0 || to >= items.length) return items;
  const reordered = [...items];
  [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

  const setlistId = items[0].setlist_id;
  await supabase.from("setlist_items").delete().eq("setlist_id", setlistId);
  await supabase.from("setlist_items").insert(
    reordered.map((item, index) => ({
      setlist_id: setlistId, position: index + 1, song_id: item.song_id,
      transpose: item.transpose, capo: item.capo, note: item.note,
    })),
  );
  return reordered.map((item, index) => ({ ...item, position: index + 1 }));
}

export async function setItemTranspose(setlistId: string, position: number, transpose: number) {
  await supabase.from("setlist_items").update({ transpose })
    .eq("setlist_id", setlistId).eq("position", position);
}

export async function removeItem(setlistId: string, position: number) {
  await supabase.from("setlist_items").delete()
    .eq("setlist_id", setlistId).eq("position", position);
}

/** Sharing is a link, not an invitation: a token on the row, and the read
 *  policy already honours it. */
export async function shareSetlist(id: string) {
  const token = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  await supabase.from("setlists").update({ share_token: token }).eq("id", id);
  return `${location.origin}/setlist/${id}?k=${token}`;
}

/** Remove a setlist. Items go with it through the foreign key. */
export async function deleteSetlist(id: string): Promise<void> {
  const { error } = await supabase.from("setlists").delete().eq("id", id);
  if (error) throw error;
}

/** Stop sharing: the link stops working immediately for everyone. */
export async function unshareSetlist(id: string): Promise<void> {
  const { error } = await supabase
    .from("setlists").update({ share_token: null }).eq("id", id);
  if (error) throw error;
}
